import { TypeAheadFilter } from '@components/table/filters/TypeAheadFilter';
import { TableFilterTypeAheadFilterExtendedProps } from '@components/table/filters/TypeaheadFilterProps.type';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

import { useSearchPaginatedBrokerages } from '@services/brokerage';
import { useEntityMetadata } from '@services/entityMetadata';
import { useStore } from '@store/GlobalStore';
import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { shallow } from 'zustand/shallow';

export interface BrokerageTypeaheadFilterProps extends TableFilterTypeAheadFilterExtendedProps {}

export const BrokerageTypeaheadFilter = (componentProps: BrokerageTypeaheadFilterProps) => {
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { value, disabled, childrenFields, selectedValues } = componentProps;
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { searchBrokerage, fetchNextPage, isLoading, isFetching, isPending, isFetchingNextPage } =
    useSearchPaginatedBrokerages(isInternalUser, {
      sort: '+name',
      pageLimit: 50,
      name: searchText,
    });

  // this means filter has been applied by the caller but we need to ensure we have the label since this filter is paginated
  const { entityMetadata } = useEntityMetadata(
    {
      'brokerage.id': [value],
    },
    !selectedFromList && !!value ? true : false,
  );

  const selectedItemLabel = useMemo(() => {
    const brokerage = entityMetadata?.brokerage?.[0];
    if (brokerage) {
      return `${brokerage.label}`;
    }
  }, [entityMetadata]);

  const handleDebouncedInputChange = useDebouncedCallback((value) => {
    setSearchText(value);
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  const items =
    searchBrokerage?.pages
      ?.flatMap((page) => page.items)
      ?.map((ip) => {
        return {
          key: ip.id,
          value: ip.id,
          label: ip.name,
        };
      }) ?? [];

  useEffect(() => {
    if (!value) {
      setSearchText('');
    }
  }, [value]);

  const disableIfChildren = childrenFields?.some((child: string) => {
    if (selectedValues?.[child]) {
      return true;
    }
  });

  return (
    <TypeAheadFilter
      value={value}
      defaultValue={value}
      disabled={disabled || disableIfChildren}
      label={componentProps.label}
      options={items ?? []}
      isMultiple={componentProps?.fieldConfig?.selectField?.multiSelect}
      limitTags={1}
      selectedItemDefaultLabel={!selectedFromList ? selectedItemLabel : undefined}
      onChange={(value: any) => {
        setSelectedFromList(true);
        componentProps.onChange(value);
      }}
      helperText={disableIfChildren ? `Multiselect disabled due to dependent value selection` : undefined}
      onInputChange={(_, value) => {
        handleDebouncedInputChange(value);
      }}
      loading={isLoading || isFetching || isFetchingNextPage || isPending}
      slotProps={{
        listbox: {
          onScroll: (e) => {
            handleScrollToBottom(e, fetchNextPage);
          },
          style: {
            maxHeight: '300px',
          },
        },
      }}
    />
  );
};
